<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import VerticalScheduler from './VerticalScheduler.vue';
import ContactsAPI from 'dashboard/api/contacts';
import { getEventDateRange } from 'dashboard/routes/dashboard/planning/events/getEventDateRange';
import { format, startOfDay } from 'date-fns';

const store = useStore();
const { updateUISettings } = useUISettings();

const selectedDate = ref(new Date());
const selectedSpaceId = ref(null);
const spaces = useMapGetter('spaces/getSpaces');
const currentChat = useMapGetter('getSelectedChat');
const dateInput = ref(null);

const formattedDate = computed(() => {
  return format(selectedDate.value, 'EEEE, MMMM d, yyyy');
});

const closePanel = () => {
  updateUISettings({ is_scheduler_panel_open: false });
};

const pickFocusEvent = events => {
  const dated = (events || [])
    .map(event => ({ event, range: getEventDateRange(event) }))
    .filter(
      ({ range }) => range?.start && !Number.isNaN(range.start.getTime())
    );

  if (!dated.length) return null;

  const today = startOfDay(new Date());
  const upcoming = dated
    .filter(({ range }) => startOfDay(range.start) >= today)
    .sort((a, b) => a.range.start - b.range.start);

  if (upcoming.length) return upcoming[0];

  return dated.sort((a, b) => b.range.start - a.range.start)[0];
};

const spaceForEvent = event => {
  const resourceIds = new Set(
    (event?.allocated_resources || [])
      .map(resource => resource.resource_id)
      .filter(Boolean)
      .map(String)
  );

  return (
    spaces.value.find(space => resourceIds.has(String(space.resource_id))) ||
    null
  );
};

const focusContactEvent = async () => {
  const contactId = currentChat.value?.meta?.sender?.id;
  if (!contactId) return;

  try {
    const { data } = await ContactsAPI.getContactEvents(
      contactId,
      currentChat.value?.id
    );
    const focus = pickFocusEvent(data.data || []);
    if (!focus) return;

    selectedDate.value = startOfDay(focus.range.start);

    const space = spaceForEvent(focus.event);
    if (space) selectedSpaceId.value = space.id;
  } catch {
    // Keep the default date/space when events cannot be loaded
  }
};

onMounted(async () => {
  if (spaces.value.length === 0) {
    await store.dispatch('spaces/get');
  }
  await focusContactEvent();
  if (spaces.value.length > 0 && !selectedSpaceId.value) {
    selectedSpaceId.value = spaces.value[0].id;
  }
});

watch(spaces, newSpaces => {
  if (newSpaces.length > 0 && !selectedSpaceId.value) {
    selectedSpaceId.value = newSpaces[0].id;
  }
});

const handleDateChange = event => {
  selectedDate.value = new Date(event.target.value);
};

const openDatePicker = () => {
  dateInput.value?.showPicker();
};

const goToToday = () => {
  selectedDate.value = new Date();
};

const goToPreviousDay = () => {
  const newDate = new Date(selectedDate.value);
  newDate.setDate(newDate.getDate() - 1);
  selectedDate.value = newDate;
};

const goToNextDay = () => {
  const newDate = new Date(selectedDate.value);
  newDate.setDate(newDate.getDate() + 1);
  selectedDate.value = newDate;
};
</script>

<template>
  <div class="h-full flex flex-col w-full text-n-slate-12">
    <div
      class="flex items-center justify-between px-4 py-2 border-b border-n-weak h-12"
    >
      <div class="flex items-center justify-between gap-2 flex-1 relative">
        <button
          class="font-medium text-sm text-n-slate-12 hover:text-n-brand transition-colors cursor-pointer flex items-center gap-1.5"
          @click="openDatePicker"
        >
          <fluent-icon icon="calendar" size="16" />
          <span>{{ formattedDate }}</span>
          <fluent-icon icon="chevron-down" size="12" class="text-n-slate-10" />
        </button>
        <input
          ref="dateInput"
          type="date"
          :value="format(selectedDate, 'yyyy-MM-dd')"
          class="reset-base absolute opacity-0 pointer-events-none"
          @change="handleDateChange"
        />

        <div class="flex items-center">
          <button
            class="p-1.5 hover:bg-n-alpha-2 rounded transition-colors"
            @click="closePanel"
          >
            <fluent-icon icon="dismiss" size="16" class="text-n-slate-11" />
          </button>
        </div>
      </div>
    </div>

    <div class="p-4 border-b border-n-weak">
      <div class="flex items-center gap-2">
        <button
          class="px-3 py-2 text-xs font-medium bg-n-alpha-2 hover:bg-n-alpha-3 border border-n-weak rounded transition-colors"
          @click="goToToday"
        >
          Today
        </button>
        <div class="flex items-center gap-1">
          <button
            class="p-1.5 hover:bg-n-alpha-2 rounded transition-colors"
            @click="goToPreviousDay"
          >
            <fluent-icon
              icon="chevron-left"
              size="16"
              class="text-n-slate-11"
            />
          </button>
          <button
            class="p-1.5 hover:bg-n-alpha-2 rounded transition-colors"
            @click="goToNextDay"
          >
            <fluent-icon
              icon="chevron-right"
              size="16"
              class="text-n-slate-11"
            />
          </button>
        </div>
        <select
          v-model="selectedSpaceId"
          class="text-sm bg-n-alpha-2 border border-n-weak rounded p-2 focus:outline-none focus:border-n-brand !mb-0"
        >
          <option v-for="space in spaces" :key="space.id" :value="space.id">
            {{ space.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Scheduler Component -->
    <VerticalScheduler
      v-if="selectedSpaceId"
      :space-id="selectedSpaceId"
      :date="selectedDate"
    />
  </div>
</template>
